import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AlertTriangle, TrendingDown, DollarSign, Clock, CheckCircle, Activity, Zap, MessageSquare, Send } from 'lucide-react';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PredictiveAlertsDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('ALL');
  const [sendingNotification, setSendingNotification] = useState(null);
  const [notificationPhone, setNotificationPhone] = useState('');

  useEffect(() => {
    loadAlerts();
    loadAnalytics();
  }, [filterPriority]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const url = filterPriority === 'ALL' 
        ? `${API_URL}/api/predictive-alerts/active`
        : `${API_URL}/api/predictive-alerts/active?priority=${filterPriority}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setAlerts(data.alerts);
        setSummary(data.summary);
      }
    } catch (err) {
      console.error('Failed to load alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/predictive-alerts/analytics/summary`);
      const data = await response.json();
      if (data.success) {
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/predictive-alerts/acknowledge/${alertId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ acknowledged_by: 'Current User' })
      });
      
      if (response.ok) {
        loadAlerts();
      }
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const handleSendNotification = async (alertId, phone) => {
    if (!phone) {
      alert('Please enter a phone number');
      return;
    }
    
    setSendingNotification(alertId);
    try {
      const response = await fetch(
        `${API_URL}/api/predictive-alerts/notify/${alertId}?to_number=${encodeURIComponent(phone)}&channel=sms`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        alert(data.notification?.mocked 
          ? 'Notification queued (mock mode - Twilio trial)' 
          : 'Notification sent successfully!'
        );
      } else {
        alert(`Failed to send notification: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
      alert('Failed to send notification');
    } finally {
      setSendingNotification(null);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'P1_CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'P2_HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'P3_MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'P4_LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'P1_CRITICAL') return <AlertTriangle className="w-4 h-4 text-red-600" />;
    if (priority === 'P2_HIGH') return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    if (priority === 'P3_MEDIUM') return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
    return <AlertTriangle className="w-4 h-4 text-blue-600" />;
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatTime = (hours) => {
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    return `${hours.toFixed(1)} hrs`;
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Predictive Alerts Dashboard</h1>
        <p className="text-gray-600 mt-1">AI-powered incident prediction & early intervention</p>
      </div>

      {/* Analytics Summary Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">Active Alerts</p>
                  <p className="text-3xl font-bold text-blue-900">{analytics.metrics.active_alerts}</p>
                  <p className="text-xs text-blue-600 mt-1">of {analytics.metrics.total_alerts} total</p>
                </div>
                <Activity className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Revenue Saved</p>
                  <p className="text-3xl font-bold text-green-900">{formatCurrency(analytics.metrics.revenue_saved)}</p>
                  <p className="text-xs text-green-600 mt-1">by early intervention</p>
                </div>
                <DollarSign className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-800">Prediction Accuracy</p>
                  <p className="text-3xl font-bold text-purple-900">{analytics.metrics.prediction_accuracy}%</p>
                  <p className="text-xs text-purple-600 mt-1">avg confidence: {analytics.metrics.avg_confidence}%</p>
                </div>
                <Zap className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-800">MTTR Improvement</p>
                  <p className="text-3xl font-bold text-orange-900">{analytics.metrics.mttr_improvement_percentage.toFixed(0)}%</p>
                  <p className="text-xs text-orange-600 mt-1">faster resolution</p>
                </div>
                <TrendingDown className="w-10 h-10 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Priority Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Filter by Priority:</span>
            {['ALL', 'P1_CRITICAL', 'P2_HIGH', 'P3_MEDIUM', 'P4_LOW'].map(priority => (
              <Button
                key={priority}
                onClick={() => setFilterPriority(priority)}
                variant={filterPriority === priority ? 'default' : 'outline'}
                size="sm"
              >
                {priority === 'ALL' ? 'All' : priority.replace('_', ' ')}
                {summary && priority !== 'ALL' && (
                  <span className="ml-1 text-xs">({summary.by_priority[priority]})</span>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              Loading alerts...
            </CardContent>
          </Card>
        ) : alerts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-700 font-semibold">No Active Alerts</p>
              <p className="text-sm text-gray-500 mt-1">All systems operating normally</p>
            </CardContent>
          </Card>
        ) : (
          alerts.map((alert) => (
            <Card key={alert.alert_id} className="border-l-4" style={{
              borderLeftColor: 
                alert.priority === 'P1_CRITICAL' ? '#dc2626' :
                alert.priority === 'P2_HIGH' ? '#ea580c' :
                alert.priority === 'P3_MEDIUM' ? '#ca8a04' : '#2563eb'
            }}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      {getPriorityIcon(alert.priority)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg text-gray-900">{alert.station_name}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(alert.priority)}`}>
                          {alert.priority.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-gray-500">Score: {alert.priority_score}/100</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-semibold">{alert.incident_type.replace(/_/g, ' ')}</span>
                        {' • '}
                        <span>Station ID: {alert.station_id}</span>
                      </p>
                      
                      {/* Impact Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500">Revenue at Risk</p>
                          <p className="text-sm font-bold text-red-600">
                            {formatCurrency(alert.predicted_impact.revenue_at_risk)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Est. Downtime</p>
                          <p className="text-sm font-bold text-orange-600">
                            {formatTime(alert.predicted_impact.estimated_downtime)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Affected Sessions</p>
                          <p className="text-sm font-bold text-purple-600">
                            {alert.predicted_impact.affected_sessions}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Confidence</p>
                          <p className="text-sm font-bold text-blue-600">{alert.confidence}%</p>
                        </div>
                      </div>

                      {/* Pattern Analysis */}
                      {alert.pattern_analysis.recurrence_pattern && (
                        <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                          <p className="text-xs text-blue-800">
                            <strong>Pattern Detected:</strong> {alert.pattern_analysis.recurrence_pattern} recurrence
                            {' • '}
                            Occurred {alert.pattern_analysis.historical_occurrences} times before
                            {' • '}
                            Avg MTTR: {alert.pattern_analysis.average_mttr} min
                          </p>
                        </div>
                      )}

                      {/* Recommended Actions */}
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Recommended Actions:</p>
                        <ul className="space-y-1">
                          {alert.recommended_actions.map((action, idx) => (
                            <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                              <span className="text-blue-500 mt-0.5">→</span>
                              <span>
                                <strong>P{action.priority}:</strong> {action.action}
                                <span className="text-gray-400"> ({action.estimated_time} min)</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    {alert.status === 'PREDICTED' && (
                      <Button
                        onClick={() => handleAcknowledge(alert.alert_id)}
                        size="sm"
                        variant="outline"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Acknowledge
                      </Button>
                    )}
                    
                    {/* Send SMS/WhatsApp Notification */}
                    <div className="flex flex-col gap-1">
                      <Input
                        type="tel"
                        placeholder="+91..."
                        className="text-xs h-8 w-32"
                        onChange={(e) => setNotificationPhone(e.target.value)}
                      />
                      <Button
                        onClick={() => handleSendNotification(alert.alert_id, notificationPhone)}
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        disabled={sendingNotification === alert.alert_id}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        {sendingNotification === alert.alert_id ? 'Sending...' : 'Send SMS'}
                      </Button>
                    </div>
                    
                    {alert.expected_occurrence && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Expected in</p>
                        <p className="text-sm font-semibold text-gray-700">
                          {Math.round((new Date(alert.expected_occurrence) - new Date()) / (1000 * 60 * 60))}hrs
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Summary Stats */}
      {summary && alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Alert Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Alerts</p>
                <p className="text-2xl font-bold text-gray-900">{summary.total_alerts}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Critical (P1)</p>
                <p className="text-2xl font-bold text-red-600">{summary.by_priority.P1_CRITICAL}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Revenue at Risk</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(summary.total_revenue_at_risk)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Avg Confidence</p>
                <p className="text-2xl font-bold text-blue-600">{summary.avg_confidence.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PredictiveAlertsDashboard;
